import { AlertTriangle, Bot, FileWarning } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { cn } from '@insightai/ui'

/**
 * §5.3 — the three legally load-bearing disclosure labels.
 *
 * The exact strings live here as exported constants, and nowhere else, because
 * three separate consumers must agree on them:
 *   · the components below (what users see)
 *   · the QA-08 Playwright compliance suite (what CI asserts on every route)
 *   · the export pipelines (PDF/PPTX/CSV must carry the same text — AC-40 covers
 *     exports and API responses, not just views)
 *
 * Editing a string here is therefore a deliberate, reviewable act that updates
 * the assertion and the UI together. Duplicating one is how "100% of views"
 * quietly becomes 97%.
 */
export const DISCLOSURE_TEXT = {
  /** BR-PS-003, RC-FR-009 → AC-77: 100% of What-If responses. */
  scenarioInterpretation: '⚠ Scenario Interpretation — Not a Research Finding',
  /** AP-FR-003 → AC-40: 100% of views, exports and API responses. */
  simulatedResponses: '⚠ AI-Simulated Responses — Not Real Consumers',
  /** RG-FR-008 → AC-34: every report page and slide, zero exceptions. */
  aiDisclosure: 'AI-Generated Analysis — Reviewed by a Research Lead',
} as const

export type DisclosureKind = keyof typeof DISCLOSURE_TEXT

/** Machine-readable list for the compliance suite to iterate. */
export const DISCLOSURE_REQUIREMENTS: Record<
  DisclosureKind,
  { text: string; requirement: string; acceptanceCriterion: string }
> = {
  scenarioInterpretation: {
    text: DISCLOSURE_TEXT.scenarioInterpretation,
    requirement: 'BR-PS-003 / RC-FR-009',
    acceptanceCriterion: 'AC-77',
  },
  simulatedResponses: {
    text: DISCLOSURE_TEXT.simulatedResponses,
    requirement: 'AP-FR-003',
    acceptanceCriterion: 'AC-40',
  },
  aiDisclosure: {
    text: DISCLOSURE_TEXT.aiDisclosure,
    requirement: 'RG-FR-008',
    acceptanceCriterion: 'AC-34',
  },
}

const ICONS: Record<DisclosureKind, ComponentType<{ className?: string }>> = {
  scenarioInterpretation: AlertTriangle,
  simulatedResponses: Bot,
  aiDisclosure: FileWarning,
}

const TONES: Record<DisclosureKind, string> = {
  scenarioInterpretation: 'border-state-warning-border bg-state-warning-surface text-state-warning',
  simulatedResponses: 'border-state-critical-border bg-state-critical-surface text-state-critical',
  aiDisclosure: 'border-ai-generated-border bg-ai-generated-surface text-ai-generated',
}

export interface DisclosureLabelProps {
  kind: DisclosureKind
  /** Extra context after the mandatory text — never a replacement for it. */
  detail?: ReactNode
  className?: string
}

/**
 * The label itself.
 *
 * Deliberate design choices:
 *   · there is no `children` prop and no way to change `text` — a caller can add
 *     `detail` but cannot alter or suppress the mandated sentence
 *   · `role="note"` plus `data-disclosure` gives the compliance suite a stable
 *     selector that does not depend on matching styled text
 *   · `print:` styles are included because AC-34 covers slides and PDFs, and a
 *     label hidden by print CSS is exactly the kind of gap sampling misses
 */
export function DisclosureLabel({ kind, detail, className }: DisclosureLabelProps) {
  const Icon = ICONS[kind]
  return (
    <p
      role="note"
      data-disclosure={kind}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-caption font-medium',
        'print:border-black print:bg-white print:text-black',
        TONES[kind],
        className,
      )}
    >
      <Icon aria-hidden className="mt-px size-3.5 shrink-0" />
      <span>
        {DISCLOSURE_TEXT[kind]}
        {detail && <span className="ms-1 font-normal opacity-90">{detail}</span>}
      </span>
    </p>
  )
}

export const ScenarioInterpretationLabel = (props: Omit<DisclosureLabelProps, 'kind'>) => (
  <DisclosureLabel kind="scenarioInterpretation" {...props} />
)

export const SimulatedResponsesLabel = (props: Omit<DisclosureLabelProps, 'kind'>) => (
  <DisclosureLabel kind="simulatedResponses" {...props} />
)

export const AIDisclosureLabel = (props: Omit<DisclosureLabelProps, 'kind'>) => (
  <DisclosureLabel kind="aiDisclosure" {...props} />
)
